import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';

import AcceptConcurrentMessagingSession from 'actions/concurrent_messaging/accept_concurrent_messaging_session';
import Button, { ButtonTypes } from 'components/common/button';
import ChatIconOutline from 'components/lib/icons/chat_icon_outline';
import connect from 'components/lib/connect';
import DeclineConcurrentMessagingSession from 'actions/concurrent_messaging/decline_concurrent_messaging_session';
import { getOfferedSessionForAgent } from 'actions/concurrent_messaging/session_finder';
import { iconClassFromSessionType } from 'components/lib/icon_classes';
import InteractionType from 'models/interaction_type';
import Notification from './notification';
import { RoutingStatus } from 'models/routing_queue_item';
import { withTranslation } from 'react-i18next';

function IncomingSessionNotification({
  customerName,
  type,
  messageText,
  notificationId,
  onCancel,
  onAccept,
  showNotification,
  titleText,
}) {
  if (!showNotification) {
    return null;
  }

  return (
    <Notification contentClasses="incomingConcurrentMessageNotification" notificationId={notificationId}>
      <div className="conversationNotification-topic">
        {renderIcon()}
        <div className="conversationNotification-notificationType">{titleText}</div>
        <div className="conversationNotification-divider" />
        <div className="conversationNotification-topic-container">
          <div className="conversationNotification-topic-heading">{customerName}</div>
          <div className="conversationNotification-divider" />
          <div className="conversationNotification-topic-body">{messageText}</div>
        </div>
      </div>
      <div>
        <Button data-aid="notification-button-decline" contrast buttonType={ButtonTypes.TEXT} onClick={onCancel}>
          Decline
        </Button>
        <Button
          data-aid="notification-button-primary"
          contrast
          buttonType={ButtonTypes.PRIMARY}
          onClick={onAcceptClick}
        >
          Accept
        </Button>
      </div>
    </Notification>
  );

  function onAcceptClick(ev) {
    ev.stopPropagation();
    onAccept();
  }

  function renderIcon() {
    return type === InteractionType.CHAT ? (
      <ChatIconOutline className="conversationNotification-chat-icon" />
    ) : (
      <i className={iconClassFromSessionType(type)} />
    );
  }
}

IncomingSessionNotification.propTypes = {
  customerName: PropTypes.string,
  messageText: PropTypes.string,
  notificationId: PropTypes.string,
  showNotification: PropTypes.bool,
  titleText: PropTypes.string,
  type: PropTypes.oneOf(_.values(InteractionType)),
  onAccept: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

const IncomingSessionNotificationContainer = connect(mapStateToProps, mapExecuteToProps)(IncomingSessionNotification);

function mapStateToProps(context, { t }) {
  let currentAgentId = _.get(context.getProvider('currentAgent').get(), 'id');
  let offeredSession = getOfferedSessionForAgent(context.getProvider('activeSessions'), currentAgentId);

  const messagingConfiguration = context.getProvider('messagingConfiguration').get();

  if (!offeredSession || _.get(messagingConfiguration, 'autoAssignMessagingSessions')) {
    return {
      showNotification: false,
    };
  }

  let currentLocation = context.getProvider('currentLocation').get();
  let agents = context.getProvider('agents');

  let titleText = 'Incoming';
  let messageText =
    (_.get(messagingConfiguration, 'showPreview') && _.get(offeredSession, 'messageText')) || 'New message';

  let queueItem = offeredSession.queueItem;
  if (queueItem && queueItem.status === RoutingStatus.OFFERED_ACTIVE) {
    // pull the yielding agent from the participants list. that will represent the last agent to have actually working on this item
    // if it does not exist, that means that agent has logged off, and that this item is essentially (or imminently will be) the same thing as OFFERED_REQUEUED
    let yieldedAgentId = queueItem.getYieldedAgentId();
    if (yieldedAgentId) {
      titleText = t('Assignment');
      messageText = t('has been assigned to you');

      let yieldedAgent = agents.findBy({ id: yieldedAgentId });
      let yieldedAgentName = yieldedAgent && yieldedAgent.getDisplayName();
      if (yieldedAgentName) {
        messageText = t('has been assigned to you from {{agentName}}', { agentName: yieldedAgentName });
      }
    }
  }

  return {
    currentLocation,
    customerName: offeredSession.getCustomerDisplayName() || 'Customer',
    showNotification: true,
    messageText,
    notificationId: offeredSession.id,
    titleText,
    type: _.get(offeredSession.queueItem.getLatestSession(), 'type'),
  };
}

function mapExecuteToProps(executeAction) {
  return {
    onAccept: () => executeAction(AcceptConcurrentMessagingSession),
    onCancel: () => executeAction(DeclineConcurrentMessagingSession),
  };
}

export { IncomingSessionNotification, IncomingSessionNotificationContainer };
export default withTranslation()(IncomingSessionNotificationContainer);



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/notification/incoming_session_notification.jsx